import React, { useEffect, useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { styled } from '@mui/material/styles'
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Skeleton,
  CircularProgress,
  useMediaQuery,
  Paper,
  Box,
  alpha,
} from '@mui/material'

import { grey } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'

import { JsEvent, withScrollingTarget } from '../../../adapters/Eventing'
import { isWindowBottom, queryKnownMatchMedia } from '../../../adapters/windowUtils'
import { System, debounce } from '../../../Reusable'

import { RootState } from '../../../store'
import { PostItemView } from './ServerPostComponent'
import {
  END_OF_FEED,
  getSearchCmdDispatch,
  onLikeLoadRequestHandler,
  onLikeServerRequest,
} from '../../../adapters/FeedAdapter'
import {
  searchActions,
  SearchCmdDispatch,
  SearchMsg,
  SearchState,
  tryGetCurrentItems,
} from '../../../store/search-slice'
import { useWindowScrollListener } from '../../../util/ReactReusable'
import { AppNavBarHeight } from '../../../components/appBar/AppNavBar'
import { VideoView } from './VidComponent'
import FeedIcon from '@mui/icons-material/RssFeed'

// Increase the base max width to allow more content on larger screens
export const MAX_WIDTH = 820 // Increased from 650

const PREFIX = 'CDS'

export const classes = {
  feed: `${PREFIX}-feed`,
  shimmer: `${PREFIX}-shimmer`,
  hover: `${PREFIX}-hover`,
  actionBarIconText: `${PREFIX}-actionBarIcons`,
  like: `${PREFIX}-like`,
  loadIcon: `${PREFIX}-loadIcon`,
  share: `${PREFIX}-share`,
  sharePostCopy: `${PREFIX}-sharePostCopy`,
  container: `${PREFIX}-container`,
  containerShimmer: `${PREFIX}-containerShimmer`,
  blur: `${PREFIX}-blur`,
  stickyHeader: `${PREFIX}-stickyHeader`,
  bannerText: `${PREFIX}-bannerText`,
  endOfFeed: `${PREFIX}-endOfFeed`,
  feedHeader: `${PREFIX}-feedHeader`,
}

// exported only for MvuFeed, remove later if we can change over to it
// TODO: review if some of these classes need to be migrated into ServerPostComponent
export const Root = styled('div')(({ theme }) => ({
  [`& .${classes.feed}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  [`& .${classes.shimmer}`]: {
    top: 70,
  },
  [`& .${classes.hover}`]: {
    cursor: 'pointer',
  },
  [`& .${classes.actionBarIconText}`]: {
    // lineHeight: '.5em'
    fontSize: '.75em',
  },
  [`& .${classes.like}`]: {
    display: 'flex',
    width: 175,
    backgroundColor: 'transparent',
    transition: theme.transitions.create(['color', 'background-color', 'padding-left'], {
      duration: theme.transitions.duration.complex,
    }),
    cursor: 'pointer',
  },

  [`& .${classes.loadIcon}`]: {
    alignSelf: 'center',
    marginBottom: 20,
  },

  [`& .${classes.share}`]: {
    display: 'flex',
    width: 175,
    backgroundColor: 'transparent',
    transition: theme.transitions.create(['color', 'background-color', 'padding-left'], {
      duration: theme.transitions.duration.complex,
    }),
    cursor: 'pointer',
  },

  [`& .${classes.sharePostCopy}`]: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.primary.contrastText,
    alignItems: 'center',
    paddingLeft: 10,
    cursor: 'default',
    pointerEvents: 'none',
  },
  [`& .${classes.container}`]: {
    marginBottom: 20,
    boxShadow: '1px 1px 10px #dbdbdb',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 400, // Increased from 318 for better mobile display
      margin: '0 auto 20px auto', // Center the container on mobile
      width: '100%',
    },
    // Add responsive widths for larger screens
    [theme.breakpoints.up('md')]: {
      maxWidth: MAX_WIDTH,
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: MAX_WIDTH + 50,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: MAX_WIDTH + 150,
    },
    '@media (min-width: 2500px)': {
      maxWidth: MAX_WIDTH + 250,
    },
    '@media (min-width: 3500px)': {
      maxWidth: MAX_WIDTH + 350,
    },
    '@media (min-width: 3840px)': {
      maxWidth: MAX_WIDTH + 450,
    },
  },
  [`& .${classes.containerShimmer}`]: {
    width: '85vw',
    maxWidth: MAX_WIDTH,
    '@media (min-width: 2500px)': {
      maxWidth: MAX_WIDTH + 250,
    },
    '@media (min-width: 3500px)': {
      maxWidth: MAX_WIDTH + 350,
    },
    '@media (min-width: 3840px)': {
      maxWidth: MAX_WIDTH + 450,
    },
  },
  [`& .${classes.blur}`]: {
    position: 'relative',
    width: '100%',
    height: '80px',
    marginTop: '-60px',
    backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, white 80%, white 100%)',
  },
  [`& .${classes.stickyHeader}`]: {
    position: 'sticky',
    top: AppNavBarHeight,
    width: '100%',
    zIndex: 999,
    marginBottom: 10,
  },
  [`& .${classes.bannerText}`]: {
    padding: 5,
  },
  [`& .${classes.endOfFeed}`]: {
    marginBottom: 10,
  },
  [`& .${classes.feedHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}))

/*
    Displayed while loading
*/
// exported for mvuFeed
export const Shimmer = () => {
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <Card raised={true} className={`${classes.container} ${classes.containerShimmer}`}>
      <CardHeader //TODO get avatar from backend
        avatar={<Skeleton animation="wave" variant="circular" width={40} height={40} />}
        action={null}
        title={<Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 6 }} />}
        subheader={<Skeleton animation="wave" height={10} width="40%" />}
        min-width={'90vw'} // Prevent card from shrinking too much
      />
      <Skeleton height={sm ? 300 : 500} animation="wave" variant="rectangular" />
      <CardContent>
        <React.Fragment>
          <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" />
        </React.Fragment>
      </CardContent>
    </Card>
  )
}

export const SS_PREF = 'CDS'

const dummyItems = new Array(9).fill(0)

// hot reload is causing nextUrl to fire getting the next items
const Feed = () => {
  // const [postItems, setPostItems] = useState<ServerPostItem[]>([]);
  // const [nextUrl, setNextUrl] = useState<string>("");
  const userSearchState = useSelector((state: RootState) => state.search)

  const currentItems = tryGetCurrentItems(userSearchState)
  const initiallyLoading = userSearchState.currentShownKey === undefined || currentItems === undefined

  let isSmallDevice = queryKnownMatchMedia('isSmallDevice') ?? false
  // console.log("Feed", { items: postItems.length, hasInvokeRef: searchInvokeRef.current !== null });
  // console.log("Feed", currentSearch);

  const dispatch = useDispatch()
  // TODO: update with proper dispatch creator
  let cmdDispatch = getSearchCmdDispatch(dispatch)

  // Reset scroll
  useEffect(() => {
    // console.log('Feed re-init');
    window.scrollTo(0, 0)
    //whoAmI(newToken).then(res=>console.log(res));
    return () => {
      window.scrollTo(0, 0)
    }
  }, [])

  const onLikeLoadRequest = useCallback(onLikeLoadRequestHandler(dispatch, 'CDS'), [dispatch])

  const onLikeClick =
    (dispatch: Function, cmdDispatch: SearchCmdDispatch) =>
    (ss: SearchState) =>
    (itemId: string): Promise<void> | undefined => {
      return onLikeServerRequest(dispatch, cmdDispatch)(ss)(itemId)
    }

  // TODO: missing dependencies?
  useEffect(() => {
    // If it's our initial load without cache, load with shimmer
    console.log(
      'CDS reloading items',
      JSON.stringify({ loadingShownKey: userSearchState.loadingShownKey, inFlight: userSearchState.inFlightSearch })
    )
    if (initiallyLoading) {
      // determine if we have items loaded to show, if not load them
      let cItems = tryGetCurrentItems(userSearchState)
      if (userSearchState.currentShownKey === undefined || cItems === undefined) {
        let msg: SearchMsg = {
          type: 'PostsLoadRequestedMsg',
          searchInput: '',
        }
        dispatch(searchActions.update({ msg, cmdDispatch }))
        // setLoading(true);
        // let pfa: FetchInit = { state: "init" };
        // loadPosts("", pfa);
      } else {
        console.log('')
      }
    } else {
      console.log('CDS feed with already loading')
    }
  }, []) // adding the requested dependencies make this constantly refire

  // Corrected onScrollBottom definition and useCallback
  const onScrollBottom = useCallback(
    debounce((_event?: JsEvent) => {
      // Accept an optional event argument
      // Inside the debounced function, use userSearchState from the useCallback's closure.
      const latestCurrentItems = tryGetCurrentItems(userSearchState)
      const latestInitiallyLoading = userSearchState.currentShownKey === undefined || latestCurrentItems === undefined

      if (isWindowBottom()) {
        console.log('isWindowBottom triggered. latestInitiallyLoading:', latestInitiallyLoading)
        if (!latestInitiallyLoading) {
          console.log('Attempting to load more. Next URL:', latestCurrentItems?.nextUrl)
          if (latestCurrentItems?.nextUrl && latestCurrentItems.nextUrl !== END_OF_FEED) {
            window.scrollBy(0, -10) // Scroll up slightly
            let msg: SearchMsg = {
              type: 'UserScrollToBottomMsg',
              key: userSearchState.currentShownKey, // From closed-over userSearchState
            }
            dispatch(searchActions.update({ msg, cmdDispatch }))
          } else {
            console.log('Not loading more: No nextUrl or end of feed reached.')
          }
        } else {
          console.log('Not loading more: latestInitiallyLoading is true.')
        }
      }
    }, 1000),
    [userSearchState, dispatch, cmdDispatch] // Corrected dependencies
  )

  // Corrected useWindowScrollListener call (around line 315)
  useWindowScrollListener(
    withScrollingTarget((event: JsEvent) => {
      // withScrollingTarget provides an 'event' argument
      onScrollBottom(event) // Pass the event argument to the debounced function
    })
  )

  let searchBannerText = userSearchState.currentShownKey
    ? `Search results for "${userSearchState.currentShownKey}"`
    : ''

  // Only show the banner if there is a search term and we are not in the initial loading shimmer state
  let searchBanner = !initiallyLoading && userSearchState.currentShownKey ? searchBannerText : ''

  const theme = useTheme()

  let cdsView = (
    <Root data-file="CDS" data-method="Feed">
      {/* DCPS Feeds header section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          mb: 3,
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box className={classes.feedHeader} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FeedIcon
              sx={{
                mr: 1.5,
                color: theme.palette.primary.main,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
              }}
            >
              DCPS Feeds
            </Typography>
          </Box>
        </Box>

        {!!searchBanner ? (
          <header className={classes.stickyHeader}>
            <Paper>
              <p className={classes.bannerText}>{searchBanner}</p>
            </Paper>
          </header>
        ) : undefined}
        <div className={classes.feed} style={{ width: '100%' }}>
          {/* <div>
            <VideoView sources={""} />
          </div> */}
          {(currentItems?.items ?? []).map(item =>
            item.link !== 'Video' ? (
              <PostItemView
                key={item.id}
                postItem={item}
                isSmallDevice={isSmallDevice}
                onLikeUnlikeClick={itemId => onLikeClick(dispatch, cmdDispatch)(userSearchState)(itemId)}
                onLoadRequest={() => onLikeLoadRequest(item)}
                searchKeyword={userSearchState.currentShownKey}
              />
            ) : (
              <VideoView sources={item.link} key={`${item.id}-vid`} />
            )
          )}
          {userSearchState.loadingShownKey && <CircularProgress className={classes.loadIcon} />}
          {/* {(tryGetCurrentItems(userSearchState)?. === END_OF_FEED && <Typography align='center' variant='body2' style={{ marginBottom: 10 }} color={grey[400]}>End of Feed</Typography>} */}
          {currentItems && (currentItems.nextUrl === undefined || currentItems.nextUrl === END_OF_FEED) && (
            <Typography align="center" variant="body2" className={classes.endOfFeed} color={grey[400]}>
              End of Feed
            </Typography>
          )}
        </div>
      </Paper>
    </Root>
  )

  return (
    <>
      {initiallyLoading ? (
        <Root data-file="CDS" data-method="Feed" data-state="initiallyLoading">
          <div className={classes.shimmer}>
            {dummyItems.map((_, index) => (
              <Shimmer key={index} />
            ))}
          </div>
        </Root>
      ) : (
        cdsView
      )}
    </>
  )
}

export default Feed
